/**
 * The handset — install state, permissions, camera, microphone, notifications.
 *
 * Everything here exists because a PWA behaves differently on an iPhone and on
 * an Android in ways that are invisible until a rep is standing in a clinic.
 * The rules this file encodes, each of which costs a real failure if ignored:
 *
 * ★ 1 · On iOS, notifications work ONLY in an installed PWA.
 *   Safari in a tab has no Notification API at all (`typeof Notification` is
 *   "undefined" on iPhone until iOS 16.4, and even then only under
 *   display-mode: standalone). So a button that calls requestPermission() on an
 *   iPhone in a tab does nothing, forever, with no error. We detect that state
 *   and tell the rep to add the app to their Home Screen instead of firing a
 *   prompt into a void.
 *
 * ★ 2 · A denial is close to permanent, and JavaScript cannot undo it.
 *   Once "Don't Allow" is tapped, the browser will not ask again — the rep has
 *   to find it in Settings, which on iOS is four levels deep. So we NEVER ask
 *   cold. Every request is preceded by our own explanation screen, and the
 *   browser prompt only fires when the rep has already said yes to us. This is
 *   the single highest-value rule in this file.
 *
 * ★ 3 · A permission request must come from a user gesture.
 *   Called from an effect or a timer it is rejected silently on Safari and with
 *   a console warning on Chrome. Every entry point here is designed to be
 *   called from an onClick and nothing else.
 *
 * ★ 4 · The Permissions API does not exist for camera/microphone in Safari.
 *   `navigator.permissions.query({name:'camera'})` throws a TypeError there.
 *   So state is probed defensively and "unknown" is a legitimate answer we show
 *   honestly, rather than guessing and being wrong.
 *
 * ★ 5 · MediaRecorder speaks different languages on the two platforms.
 *   Chrome produces audio/webm;codecs=opus. Safari cannot record webm at all
 *   and produces audio/mp4. Hard-coding either one gives a silent empty blob on
 *   the other — and a rep discovers it when their voice note is gone.
 */

export type Platform = "ios" | "android" | "desktop" | "other";

export type PermissionState = "granted" | "denied" | "prompt" | "unknown";

export type NotificationReadiness =
  | { state: "unsupported"; why: string }
  | { state: "needs-install"; why: string }
  | { state: "prompt"; why: string }
  | { state: "granted"; why: string }
  | { state: "denied"; why: string };

/* ── what we are running on ──────────────────────────────────────────────── */

export function platform(): Platform {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent;
  /* ★ iPadOS 13+ reports itself as a Mac. maxTouchPoints is the only reliable
     way left to tell an iPad from a MacBook, and an iPad is an iOS device for
     every rule in this file. */
  const iPadOS = /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
  if (/iPad|iPhone|iPod/.test(ua) || iPadOS) return "ios";
  if (/Android/.test(ua)) return "android";
  if (/Windows|Macintosh|Linux/.test(ua)) return "desktop";
  return "other";
}

/** Installed to the Home Screen / launcher, rather than running in a tab. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const displayMode =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.matchMedia?.("(display-mode: fullscreen)").matches ||
    window.matchMedia?.("(display-mode: minimal-ui)").matches;
  /* `navigator.standalone` is the iOS-only legacy flag and is still the most
     reliable signal on an iPhone. */
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return Boolean(displayMode || iosStandalone);
}

export function isSecureForMedia(): boolean {
  /* getUserMedia and service workers both require a secure context. localhost
     counts; a LAN IP over plain http does NOT — which is exactly how testing on
     a phone over the office wifi fails while it works on the laptop. */
  return typeof window !== "undefined" && window.isSecureContext === true;
}

/* ── install ─────────────────────────────────────────────────────────────── */

type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredInstall: InstallPrompt | null = null;

/**
 * ★ Android fires beforeinstallprompt ONCE, early, and the event is only usable
 *   if preventDefault() was called on it. Miss it and the app can never show its
 *   own install button. So this is armed from main.tsx at startup, long before
 *   any screen wants it.
 *
 * ★ iOS fires nothing at all — there is no programmatic install on iPhone. The
 *   only route is Share → Add to Home Screen, by hand, which is why
 *   `installGuidance()` returns instructions rather than a button there.
 */
export function armInstallPrompt(onAvailable?: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (e: Event) => {
    e.preventDefault();
    deferredInstall = e as InstallPrompt;
    onAvailable?.();
  };
  window.addEventListener("beforeinstallprompt", handler);
  const installed = () => {
    deferredInstall = null;
    onAvailable?.();
  };
  window.addEventListener("appinstalled", installed);
  return () => {
    window.removeEventListener("beforeinstallprompt", handler);
    window.removeEventListener("appinstalled", installed);
  };
}

export const canPromptInstall = () => deferredInstall !== null;

export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredInstall) return "unavailable";
  const e = deferredInstall;
  /* Single use: the event cannot be prompted twice. */
  deferredInstall = null;
  await e.prompt();
  const { outcome } = await e.userChoice;
  return outcome;
}

/** What to tell the rep, given what this handset can actually do. */
export function installGuidance(): { canButton: boolean; steps: string[] } {
  if (isStandalone()) return { canButton: false, steps: [] };
  if (canPromptInstall()) return { canButton: true, steps: [] };
  if (platform() === "ios") {
    return {
      canButton: false,
      steps: [
        "Tap the Share button at the bottom of Safari.",
        'Scroll down and tap "Add to Home Screen".',
        'Tap "Add".',
      ],
    };
  }
  return {
    canButton: false,
    steps: ["Open the browser menu and choose Install app, or Add to Home screen."],
  };
}

/* ── notifications ───────────────────────────────────────────────────────── */

export function notificationReadiness(): NotificationReadiness {
  if (typeof window === "undefined" || !("Notification" in window)) {
    /* ★ This is the normal state for Safari on an iPhone that has not installed
       the app. It is not a broken browser and must not be reported as one. */
    if (platform() === "ios" && !isStandalone()) {
      return {
        state: "needs-install",
        why: "On an iPhone, reminders only work once RocketRep is on your Home Screen. Add it, open it from there, and turn them on.",
      };
    }
    return {
      state: "unsupported",
      why: "This browser cannot show reminders. Everything else works; you will just have to check Today yourself.",
    };
  }

  if (platform() === "ios" && !isStandalone()) {
    return {
      state: "needs-install",
      why: "On an iPhone, reminders only work once RocketRep is on your Home Screen. Add it, open it from there, and turn them on.",
    };
  }

  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return {
      state: "unsupported",
      why: "This browser cannot receive reminders in the background.",
    };
  }

  switch (Notification.permission) {
    case "granted":
      return { state: "granted", why: "Reminders are on." };
    case "denied":
      /* ★ Unrecoverable from here. Saying "try again" would be a lie. */
      return {
        state: "denied",
        why:
          platform() === "ios"
            ? "Reminders are blocked. Turn them back on in Settings → Notifications → RocketRep."
            : "Reminders are blocked. Turn them back on in the browser's site settings for this app.",
      };
    default:
      return { state: "prompt", why: "Not turned on yet." };
  }
}

/** VAPID keys travel as base64url; PushManager wants raw bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normal = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normal);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  if (!isSecureForMedia()) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch {
    return null;
  }
}

/**
 * Turn reminders on. ★ MUST be called from a click, and only after the rep has
 * already agreed on our own screen — see rule 2 at the top of this file.
 */
export async function enableNotifications(): Promise<
  { ok: true } | { ok: false; reason: NotificationReadiness }
> {
  const ready = notificationReadiness();
  if (ready.state !== "prompt" && ready.state !== "granted") return { ok: false, reason: ready };

  if (Notification.permission !== "granted") {
    const result = await Notification.requestPermission();
    if (result !== "granted") return { ok: false, reason: notificationReadiness() };
  }

  const reg = (await navigator.serviceWorker.getRegistration()) ?? (await registerServiceWorker());
  if (!reg) {
    return {
      ok: false,
      reason: { state: "unsupported", why: "The background worker did not start." },
    };
  }
  await navigator.serviceWorker.ready;

  /* The public key is served by our own API rather than baked into the bundle,
     so rotating it does not require a redeploy of the front end. */
  const keyRes = await fetch("/api/pro/push/key", { credentials: "include" });
  if (!keyRes.ok) {
    return {
      ok: false,
      reason: { state: "unsupported", why: "Reminders are not configured on the server yet." },
    };
  }
  const { publicKey } = (await keyRes.json()) as { publicKey: string };

  const existing = await reg.pushManager.getSubscription();
  /* ★ An existing subscription made with a DIFFERENT key is dead — the push
     service will reject every send with a 403 and nothing surfaces to the rep.
     Unsubscribing and re-subscribing is the only fix. */
  if (existing) {
    const same =
      existing.options?.applicationServerKey &&
      btoa(String.fromCharCode(...new Uint8Array(existing.options.applicationServerKey)))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "") === publicKey;
    if (!same) await existing.unsubscribe().catch(() => undefined);
  }

  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      /* Required true by every browser: we may not push silently. */
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const res = await fetch("/api/pro/push/subscribe", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      subscription: sub.toJSON(),
      platform: platform(),
      standalone: isStandalone(),
    }),
  });
  if (!res.ok) {
    return {
      ok: false,
      reason: { state: "unsupported", why: "Could not register this phone for reminders." },
    };
  }
  return { ok: true };
}

/* ── camera and microphone ───────────────────────────────────────────────── */

/**
 * Probe without asking. ★ Returns "unknown" rather than guessing on Safari,
 * where `navigator.permissions.query({name:'camera'})` throws outright. A
 * screen that shows "unknown" honestly is better than one that shows "allowed"
 * and then fails at the moment the rep taps record.
 */
export async function probePermission(name: "camera" | "microphone"): Promise<PermissionState> {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) return "unknown";
  try {
    const status = await navigator.permissions.query({ name: name as PermissionName });
    return status.state as PermissionState;
  } catch {
    return "unknown";
  }
}

export type MediaFailure =
  | "insecure"
  | "unsupported"
  | "denied"
  | "not-found"
  | "in-use"
  | "unknown";

export function explainMediaFailure(kind: MediaFailure, what: "camera" | "microphone"): string {
  const ios = platform() === "ios";
  switch (kind) {
    case "insecure":
      return `The ${what} only works over a secure connection. Open the app at its https address.`;
    case "unsupported":
      return `This browser will not give the app the ${what}.`;
    case "denied":
      return ios
        ? `The ${what} is blocked. Turn it back on in Settings → RocketRep, or in Safari's settings for this site.`
        : `The ${what} is blocked. Turn it back on in the browser's site settings, under Permissions.`;
    case "not-found":
      return `No ${what} found on this phone.`;
    case "in-use":
      return `Something else is using the ${what} — a call, or another app. Close it and try again.`;
    default:
      return `The ${what} did not start, and the phone did not say why.`;
  }
}

function classify(err: unknown): MediaFailure {
  const name = (err as { name?: string })?.name ?? "";
  if (name === "NotAllowedError" || name === "SecurityError") return "denied";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "not-found";
  if (name === "NotReadableError" || name === "AbortError") return "in-use";
  return "unknown";
}

async function getMedia(
  constraints: MediaStreamConstraints,
  what: "camera" | "microphone",
): Promise<{ ok: true; stream: MediaStream } | { ok: false; kind: MediaFailure; message: string }> {
  if (!isSecureForMedia()) {
    return { ok: false, kind: "insecure", message: explainMediaFailure("insecure", what) };
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return { ok: false, kind: "unsupported", message: explainMediaFailure("unsupported", what) };
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    return { ok: true, stream };
  } catch (err) {
    const kind = classify(err);
    return { ok: false, kind, message: explainMediaFailure(kind, what) };
  }
}

/** ★ Call from a click. `facingMode: environment` is the rear camera — a rep
 *  photographs a board outside a clinic, never themselves. */
export const openCamera = () =>
  getMedia({ video: { facingMode: { ideal: "environment" } }, audio: false }, "camera");

export const openMicrophone = () =>
  getMedia(
    {
      audio: {
        /* A clinic corridor is loud and a phone is held at arm's length. These
           three are honoured by Chrome and ignored harmlessly by Safari. */
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    },
    "microphone",
  );

/** Release every track. ★ Not optional: a stream left open keeps the camera
 *  light on and, on Android, stops any other app using it until the tab dies. */
export function stopStream(stream: MediaStream | null | undefined) {
  stream?.getTracks().forEach((t) => t.stop());
}

/* ── recording ───────────────────────────────────────────────────────────── */

/**
 * ★ The format negotiation that makes voice work on both platforms.
 *   Chrome and Android: audio/webm;codecs=opus. Safari and iOS: audio/mp4
 *   (AAC) — it cannot produce webm at all, and passing an unsupported mimeType
 *   to MediaRecorder throws on Chrome but is silently ignored on some Safari
 *   builds, producing a zero-byte blob the rep only discovers is empty later.
 *   So: ask the browser what it supports, in preference order, and record with
 *   the first one it admits to.
 */
const AUDIO_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

export function pickAudioMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  for (const t of AUDIO_TYPES) {
    if (MediaRecorder.isTypeSupported?.(t)) return t;
  }
  /* An empty string means "you choose" and is valid — some Safari builds
     support recording but report false for everything above. */
  return "";
}

export type Recorder = {
  stop: () => Promise<{ blob: Blob; mimeType: string; durationSec: number }>;
  cancel: () => void;
};

/** Start recording an already-open microphone stream. */
export function startRecording(stream: MediaStream): Recorder | null {
  const mimeType = pickAudioMimeType();
  if (mimeType === null) return null;

  const chunks: BlobPart[] = [];
  const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const startedAt = Date.now();
  rec.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };
  /* A timeslice makes ondataavailable fire periodically instead of only at
     stop. Without it, a tab killed mid-recording loses everything. */
  rec.start(1000);

  return {
    stop: () =>
      new Promise((resolve) => {
        rec.onstop = () => {
          const type = rec.mimeType || mimeType || "audio/webm";
          resolve({
            blob: new Blob(chunks, { type }),
            mimeType: type,
            durationSec: Math.round((Date.now() - startedAt) / 1000),
          });
        };
        rec.stop();
      }),
    cancel: () => {
      try {
        rec.stop();
      } catch {
        /* already stopped */
      }
      chunks.length = 0;
    },
  };
}

/* ── WAV, because Sarvam will not take what the browser records ──────────── */

/**
 * Convert a recorded blob to 16 kHz mono 16-bit WAV.
 *
 * ★ This exists because of a hard mismatch. MediaRecorder gives webm/opus on
 *   Chrome and mp4/aac on Safari; Sarvam's speech-to-text takes wav and mp3 and
 *   neither of those. The alternatives were putting ffmpeg on the server — a
 *   binary dependency in the deploy, for every deployment, forever — or doing
 *   it here in about sixty lines with APIs every target browser already has.
 *
 * ★ 16 kHz mono is not a compromise. Speech models resample to it anyway, so
 *   nothing recoverable is lost, and it keeps a minute of audio under 2 MB —
 *   which matters when the upload is happening on a clinic's 3G.
 *
 * ★ decodeAudioData handles both container formats for free: it is the same
 *   decoder the browser uses to play the file, so whatever it could record it
 *   can also read back.
 */
export async function toWav16kMono(blob: Blob): Promise<Blob> {
  const bytes = await blob.arrayBuffer();

  /* Safari still only exposes the prefixed constructor in some builds. */
  const Ctx: typeof AudioContext =
    window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  let decoded: AudioBuffer;
  try {
    /* ★ Safari's decodeAudioData does not return a promise in older builds —
       it only takes callbacks. Wrapped so both shapes work. */
    decoded = await new Promise<AudioBuffer>((resolve, reject) => {
      const maybe = ctx.decodeAudioData(bytes, resolve, reject) as unknown as Promise<AudioBuffer> | undefined;
      if (maybe && typeof maybe.then === "function") maybe.then(resolve, reject);
    });
  } finally {
    /* Not closing leaks an audio context per recording; a few of those and
       Safari refuses to create any more, and the microphone stops working with
       no error a rep could act on. */
    void ctx.close();
  }

  const target = 16000;
  const src = decoded.getChannelData(0);

  /* Downmix is unnecessary — getUserMedia gave us one channel — but a stream
     from a headset can arrive as two, and averaging beats silently dropping
     one, which loses the speaker if they were panned to the other side. */
  let mono = src;
  if (decoded.numberOfChannels > 1) {
    const second = decoded.getChannelData(1);
    mono = new Float32Array(src.length);
    for (let i = 0; i < src.length; i++) mono[i] = (src[i]! + second[i]!) / 2;
  }

  /* Linear resample. Speech at 16 kHz does not justify a windowed-sinc filter,
     and the artefacts a naive resampler introduces sit above the band a speech
     model listens to. */
  const ratio = decoded.sampleRate / target;
  const outLength = Math.max(1, Math.floor(mono.length / ratio));
  const out = new Int16Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const at = i * ratio;
    const lo = Math.floor(at);
    const hi = Math.min(lo + 1, mono.length - 1);
    const t = at - lo;
    const v = mono[lo]! * (1 - t) + mono[hi]! * t;
    /* Clamp before the 16-bit cast: a sample above 1.0 wraps to a loud click. */
    out[i] = Math.max(-32768, Math.min(32767, Math.round(v * 32767)));
  }

  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  const ascii = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  const dataBytes = out.length * 2;
  ascii(0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, target, true);
  view.setUint32(28, target * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits
  ascii(36, "data");
  view.setUint32(40, dataBytes, true);

  return new Blob([header, out.buffer], { type: "audio/wav" });
}

/**
 * Record → convert → upload, as one call, because every caller wants all three
 * and getting the order wrong loses the recording.
 */
export async function uploadVoiceNote(
  visitId: number,
  recorded: Blob,
): Promise<
  | { ok: true; status: string; transcribed: boolean; note: string | null }
  | { ok: false; message: string }
> {
  let wav: Blob;
  try {
    wav = await toWav16kMono(recorded);
  } catch {
    return {
      ok: false,
      message: "This phone could not prepare the recording for upload. It is still on the device — try again.",
    };
  }

  const form = new FormData();
  form.append("audio", wav, "note.wav");

  const res = await fetch(`/api/pro/visits/${visitId}/voice/audio`, {
    method: "POST",
    credentials: "include",
    body: form,
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    return { ok: false, message: body.message ?? "The recording did not upload." };
  }

  const body = (await res.json()) as {
    visit: { voice: { status: string } | null };
    transcription: { ok: boolean; note?: string; reason?: string };
  };
  return {
    ok: true,
    status: body.visit?.voice?.status ?? "KEPT",
    transcribed: body.transcription?.ok === true,
    note: body.transcription?.note ?? null,
  };
}

/* ── the "Turn on reminders" card (builder N, 29 Sep eve) ─────────────────
 * ★ Why prod had 0 PushDevice rows: `enableNotifications()` above was never
 *   called from any screen. The Me screen could SEND a test push but nothing
 *   ever ASKED for permission or subscribed. The Today card below is the ask.
 */
const REMINDER_CARD_KEY = "rr.remindersCard.dismissed";

/** True when this device already holds a live push subscription. */
export async function isPushSubscribed(): Promise<boolean> {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return false;
    const reg = await navigator.serviceWorker.getRegistration();
    return Boolean(reg && (await reg.pushManager.getSubscription()));
  } catch {
    return false;
  }
}

export function reminderCardDismissed(): boolean {
  try {
    return localStorage.getItem(REMINDER_CARD_KEY) === "1";
  } catch {
    return false;
  }
}
export function dismissReminderCard() {
  try {
    localStorage.setItem(REMINDER_CARD_KEY, "1");
  } catch {
    /* private mode — the card simply comes back next time */
  }
}
